'use client';

import {
  type UnboundThirdPartyEventInvoker,
  useThirdPartyEvent,
} from '@tuic/lib/src/client';
import { EventClickElement } from '@tuic/tracking';
import { twJoin } from 'tailwind-merge';

import { Icon, type IconName } from '../../../core-components';
import { LegacyIcon, type LegacyIconName } from '../LegacyIcon';

import type { AnchorHTMLAttributes, FC, MouseEventHandler } from 'react';

type Props = {
  icon?: LegacyIconName | IconName;
  isLegacyIcon?: boolean;
  label: string;
  clickTrackingKey?: string;
  className?: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

const parseHref = (href?: string): string | undefined => {
  if (href?.substring(0, 7) === '#event:') {
    return undefined;
  }

  return href;
};

const createOnClickHandler = (
  invoke: UnboundThirdPartyEventInvoker,
  href: Props['href'],
  onClick: Props['onClick'],
): undefined | MouseEventHandler<HTMLAnchorElement> => {
  if (onClick) {
    return onClick;
  }

  if (href?.substring(0, 7) !== '#event:') {
    return undefined;
  }

  const eventName = href.substring(7);

  return ev => {
    ev.preventDefault();
    invoke(eventName);
  };
};

export const TopNavigationItem: FC<Props> = ({
  icon,
  isLegacyIcon = true,
  label,
  href,
  onClick,
  className,
  ...props
}) => {
  const { invoke } = useThirdPartyEvent();

  return (
    <a
      data-track-click-element={EventClickElement.Button}
      data-track-click-label={label}
      className={twJoin(
        className,
        'mr-8 flex cursor-pointer items-center gap-2 text-[18px] font-normal leading-[1.4em] text-secondary-marine-100 no-underline hover:text-[#7390C5]',
      )}
      href={parseHref(href)}
      onClick={createOnClickHandler(invoke, href, onClick)}
      {...props}
    >
      {icon && isLegacyIcon && (
        <LegacyIcon name={icon as LegacyIconName} size={20} />
      )}
      {icon && !isLegacyIcon && <Icon name={icon as IconName} size="sm" />}
      <div>{label}</div>
    </a>
  );
};
